import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import TaskModal from '../components/TaskModal'
import InviteModal from '../components/InviteModal'
import { useApp } from '../context/AppContext'
import { getDaysUntil, getInitials } from '../data/mockData'


export default function EventOverview() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { events, tasks, budget, guests, meals, toggleTask, updateEvent, deleteEvent, canDelete, removeMember, currentUser } = useApp()
  const [taskModal, setTaskModal] = useState(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [isEditingOwner, setIsEditingOwner] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState(new Date())

  const getSecondaryTextColor = (color) => {
    return '#222222'
  }

  const getNavBackground = (color) => {
    if (color === '#E8C1D1') return '#AE527D'
    return color
  }

  const getStatCardColor = (color, opacity) => {
    if (color === '#E8C1D1') return '#AE527D'
    return color + opacity
  }

  const getCalendarEventColor = (color) => {
    return color + '80'
  }

  const getCalendarTaskColor = (color) => {
    switch(color) {
      case '#E8C1D1': return '#CD0D6D'
      case '#B4B48D': return '#405727'
      case '#C9845A': return '#8D5C3F'
      case '#C08A4A': return '#D6852F'
      case '#F7B98A': return '#AD8261'
      case '#2271B1': return '#184F7C'
      default: return '#98074B'
    }
  }

  let event = events.find(e => e.id === eventId)
  if (!event) return <AppLayout><p style={{ padding: 24, color: '#DF83A9' }}>Henter event...</p></AppLayout>

  // Ensure members is always an array
  if (!event.members) {
    event = { ...event, members: [] }
  }

  const eventTasks = tasks ? tasks.filter(t => t.eventId === eventId) : []
  const budgetCats = budget && budget[eventId] ? budget[eventId] : []
  const eventGuests = guests && guests[eventId] ? guests[eventId] : []
  const eventMeals = meals && meals[eventId] ? meals[eventId] : []
  const spent = budgetCats.reduce((s, c) => s + c.items.reduce((si, i) => si + i.amount, 0), 0)
  const totalBudget = event.totalBudget || budgetCats.reduce((s, c) => s + c.budget, 0) || spent
  const confirmed = eventGuests.filter(g => g.status === 'confirmed').length
  const pending = eventGuests.filter(g => g.status === 'pending').length
  const done = eventTasks.filter(t => t.done).length
  const progress = eventTasks.length > 0 ? Math.round((done / eventTasks.length) * 100) : 0
  const days = getDaysUntil(event.date)
  const secondaryTextColor = getSecondaryTextColor(event.color)

  const handleEditDetails = () => {
    setEditForm({ date: event.date, location: event.location || '', dresscode: event.dresscode || '', description: event.description || '', color: event.color })
    setIsEditingDetails(true)
  }

  const tuscanColors = [
    { hex: '#E8C1D1', name: 'Blush Pink' },
    { hex: '#C9845A', name: 'Dusty Rose' },
    { hex: '#C08A4A', name: 'Terracotta' },
    { hex: '#F7B98A', name: 'Apricot' },
    { hex: '#2271B1', name: 'Blue' },
    { hex: '#B4B48D', name: 'Olive Green' }
  ]

  const handleSaveDetails = async () => {
    if (editForm) {
      try {
        await updateEvent(eventId, editForm)
        setIsEditingDetails(false)
        setEditForm(null)
      } catch (error) {
        console.error('Error saving details:', error)
      }
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event.name}</h1>
            <span className="page-subtitle">Samlet oversigt</span>
          </div>
          {canDelete(eventId) && (
            <button onClick={() => setDeleteConfirm(true)} style={{ fontSize: 11, color: '#ff6b6b', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
              Slet event
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {/* Rediger button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button className="btn-ghost" onClick={isEditingDetails ? () => { setIsEditingDetails(false); setEditForm(null) } : handleEditDetails} style={{ fontSize: 11 }}>
            {isEditingDetails ? 'Annuller' : 'Rediger'}
          </button>
        </div>

        {/* Event details layout - 3 column grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
          {/* Left: Event dag */}
          <div className="card" style={{ background: getNavBackground(event.color), border: '4px solid ' + getNavBackground(event.color), padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 16 }}>Event dag</p>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 56, fontWeight: 700, color: '#ffffff', lineHeight: 1, marginBottom: 4 }}>
                {new Date(event.date).toLocaleDateString('da-DK', { day: 'numeric' })}
              </div>
              <div style={{ fontSize: 48, fontWeight: 700, color: '#ffffff', lineHeight: 1.1 }}>
                {new Date(event.date).toLocaleDateString('da-DK', { month: 'short' })}
              </div>
            </div>
            <p style={{ fontSize: 11, color: '#ffffff', fontWeight: 500 }}>{days} dage tilbage</p>
          </div>

          {/* Middle: Beskrivelse and Dresscode stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Beskrivelse */}
            <div className="card" style={{ background: getNavBackground(event.color), border: '4px solid ' + getNavBackground(event.color), padding: 16 }}>
              {isEditingDetails && editForm ? (
                <div>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4, display: 'block' }}>Beskrivelse</label>
                  <textarea
                    value={editForm.description}
                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Tilføj evt. noter til eventet"
                    style={{ width: '100%', padding: '6px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, fontSize: 11, minHeight: 60, fontFamily: 'inherit', resize: 'none', background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
                  />
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 8 }}>Event beskrivelse</p>
                  <p style={{ fontSize: 11, color: '#ffffff', lineHeight: '1.4' }}>{event.description || '-'}</p>
                </div>
              )}
            </div>

            {/* Dresscode */}
            <div className="card" style={{ background: getNavBackground(event.color), border: '4px solid ' + getNavBackground(event.color), padding: 16 }}>
              {isEditingDetails && editForm ? (
                <div>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4, display: 'block' }}>Dresscode</label>
                  <input
                    value={editForm.dresscode}
                    onChange={e => setEditForm({ ...editForm, dresscode: e.target.value })}
                    placeholder={'fx. Formel eller Smart Casual'}
                    style={{ width: '100%', padding: '6px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, fontSize: 11, background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
                  />
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4 }}>Dresscode</p>
                  <p style={{ fontSize: 11, color: '#ffffff' }}>{event.dresscode || '-'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Lokation and Event ansvarlig stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Lokation */}
            <div className="card" style={{ background: getNavBackground(event.color), border: '4px solid ' + getNavBackground(event.color), padding: 16 }}>
              {isEditingDetails && editForm ? (
                <div>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4, display: 'block' }}>Lokation</label>
                  <input
                    value={editForm.location}
                    onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder="fx. København"
                    style={{ width: '100%', padding: '6px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, fontSize: 11, background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
                  />
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4 }}>Lokation</p>
                  <p style={{ fontSize: 11, color: '#ffffff' }}>{event.location || '-'}</p>
                </div>
              )}
            </div>

            {/* Event ansvarlig */}
            <div className="card" style={{ background: getNavBackground(event.color), border: '4px solid ' + getNavBackground(event.color), padding: 16 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 8 }}>Event ansvarlig</p>
              {!isEditingOwner ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {event.members && event.members.filter(m => m.role === 'owner' || m.role === 'co-owner').map(m => (
                    <div key={m.id} className="avatar" style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.3)', color: '#ffffff', fontSize: 10, flexShrink: 0, border: '2px solid rgba(255,255,255,0.5)', cursor: canDelete(eventId) ? 'pointer' : 'default' }} onClick={() => canDelete(eventId) && (setIsEditingOwner(true), setSelectedOwner(event.members?.find(m => m.role === 'owner')?.name || ''))}>
                      {getInitials(m.name)}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <select
                    className="input"
                    value={selectedOwner}
                    onChange={e => setSelectedOwner(e.target.value)}
                    style={{ appearance: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.1)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.3)' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <option value={''}>Vælg arrangør</option>
                    <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                    {event.members && event.members.filter(m => m.name !== currentUser.name && m.role === 'co-owner').map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn-primary"
                      style={{ flex: 1, fontSize: 11, padding: '6px' }}
                      onClick={async e => {
                        e.stopPropagation()
                        if (!selectedOwner || !event?.members) return
                        try {
                          let updatedMembers = event.members.map(m => ({
                            ...m,
                            role: m.name === selectedOwner ? 'owner' : m.role === 'owner' ? 'co-owner' : m.role
                          }))
                          if (selectedOwner === currentUser.name && !updatedMembers.find(m => m.name === currentUser.name)) {
                            updatedMembers.push({ id: currentUser.id, name: currentUser.name, role: 'owner' })
                          }
                          const newOrganizerMember = updatedMembers.find(m => m.name === selectedOwner)
                          const newOrganizerId = newOrganizerMember?.id || currentUser.id
                          await updateEvent(eventId, { organizer: newOrganizerId, members: updatedMembers, ownerName: selectedOwner })
                          setIsEditingOwner(false)
                        } catch (error) {
                          console.error('Error updating owner:', error)
                        }
                      }}
                    >
                      Gem
                    </button>
                    <button
                      className="btn-ghost"
                      style={{ flex: 1, fontSize: 11, padding: '6px', color: '#ffffff' }}
                      onClick={e => {
                        e.stopPropagation()
                        setIsEditingOwner(false)
                      }}
                    >
                      Annuller
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isEditingDetails && editForm && (
          <div style={{ marginBottom: 20 }}>
            <button className="btn-primary" onClick={handleSaveDetails}>Gem Ændringer</button>
          </div>
        )}

        {/* Budget, Gæster og Madplan - 2 column grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          {/* Budget */}
          <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color }}>
            <div className="card-header">
              <span className="card-title">Budget overblik</span>
              <button className="btn-ghost" onClick={() => navigate(`/event/${eventId}/budget`)}>Se alt</button>
            </div>

            {/* Budget pie chart */}
            {budgetCats.length > 0 && totalBudget > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '12px 0' }}>
                <div style={{ position: 'relative', width: 180, height: 180 }}>
                  <svg width="180" height="180" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="70" cy="70" r="62" fill="none" stroke="#ffffff" strokeWidth="14" />
                    {budgetCats.map((cat, idx) => {
                      const catSpent = cat.items ? cat.items.reduce((s, i) => s + i.amount, 0) : 0
                      const percentage = (catSpent / totalBudget) * 100
                      const circumference = 2 * Math.PI * 62

                      let startAngle = 0
                      for (let i = 0; i < idx; i++) {
                        const prevCat = budgetCats[i]
                        const prevSpent = prevCat.items ? prevCat.items.reduce((s, ii) => s + ii.amount, 0) : 0
                        startAngle += (prevSpent / totalBudget) * 360
                      }

                      const endAngle = startAngle + (percentage * 360 / 100)
                      const arcLength = circumference * (percentage / 100)

                      return (
                        <circle
                          key={idx}
                          cx="70" cy="70" r="62"
                          fill="none"
                          stroke={cat.color || '#6c63ff'}
                          strokeWidth="14"
                          strokeDasharray={`${arcLength} ${circumference}`}
                          strokeDashoffset={-circumference * (startAngle / 360)}
                          strokeLinecap="round"
                        />
                      )
                    })}
                  </svg>
                  <div style={{ position: 'absolute', color: "#B8860B", top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                    <div style={{ fontSize: 42, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>{Math.round((spent / totalBudget) * 100)}%</div>
                    <div style={{ fontSize: 10, color: secondaryTextColor, marginTop: 4 }}>brugt</div>
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  <p style={{ fontSize: 10, fontWeight: 600, color: '#1a1a2e', marginBottom: 8, textAlign: 'center' }}>Kategori</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, width: '100%', padding: '0 4px' }}>
                    {budgetCats.map((cat, idx) => {
                      return (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color || '#6c63ff', flexShrink: 0 }} />
                          <span style={{ color: '#1a1a2e', fontWeight: 500 }}>{cat.name}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Gæster and Mad & Drikke stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Guests */}
            <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color, padding: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Card header */}
              <div className="card-header" style={{ borderBottom: 'none', padding: 16, paddingBottom: 12 }}>
                <span className="card-title">Gæster</span>
                {eventGuests.length > 0 && <button className="btn-ghost" onClick={() => navigate(`/event/${eventId}/guests`)}>Se alle</button>}
              </div>

              {/* Content with divider */}
              <div style={{ display: 'flex', flex: 1 }}>
                {/* Left side - Title and counts */}
                <div style={{ padding: '1px 10px 16px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minWidth: 'auto', position: 'relative' }}>
                  <div>
                    <div style={{ fontSize: 40, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>{confirmed}</div>
                    <p style={{ fontSize: 12, color: secondaryTextColor, marginBottom: 10, fontWeight: 500 }}>Accepteret</p>
                  </div>
                  {pending > 0 && (
                    <div>
                      <div style={{ fontSize: 25, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>{pending}</div>
                      <p style={{ fontSize: 12, color: '#222222', fontWeight: 500 }}>Afventer</p>
                    </div>
                  )}
                </div>

                {/* Divider line */}
                <div style={{ width: '1.5px', background: event.color, alignSelf: 'stretch', margin: '0 12px', marginBottom:'15px',marginLeft:'65px', }} />

                {/* Right side - Guest list */}
                <div style={{ padding: '10px 18px 20px 35px', flex: 1 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 8px', rowGap: '12px' }}>
                    {eventGuests.slice(0, 12).map(guest => (
                      <div key={guest.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: guest.status === 'confirmed' ? '#007a58' : guest.status === 'pending' ? '#9a5800' : '#cc2020', flexShrink: 0 }} />
                        <span style={{ color: '#1a1a2e', fontWeight: 500 }}>{guest.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Meals */}
            <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color, padding: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Card header */}
              <div className="card-header" style={{ borderBottom: 'none', padding: 16, paddingBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span className="card-title">Mad & Drikke</span>
                  <p style={{ fontSize: 10, color: '#222222', fontWeight: 600, marginTop: 4 }}>
                    {eventMeals.filter(m => m.category?.includes('ret') || m.category?.includes('Ret')).length} retter / {eventMeals.filter(m => m.category?.includes('drink') || m.category?.includes('Drink') || m.category?.includes('drikke')).length} drinks
                  </p>
                </div>
                {eventMeals.length > 0 && <button className="btn-ghost" onClick={() => navigate(`/event/${eventId}/meals`)}>Se alle</button>}
              </div>

              {/* Content with divider */}
              <div style={{ display: 'flex', flex: 1 }}>
                {/* Left side - Meals */}
                <div style={{ padding: '12px 16px 16px 16px', flex: 1, overflowY: 'auto', maxHeight: '200px' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: getCalendarTaskColor(event.color), marginBottom: 10 }}>Madretter</p>
                  {eventMeals.filter(m => m.category?.includes('ret') || m.category?.includes('Ret')).map(meal => (
                    <p key={meal.id} style={{ fontSize: 10, color: '#1a1a2e', marginBottom: 6 }}>{meal.name}</p>
                  ))}
                </div>

                {/* Divider line */}
                <div style={{ width: '1.5px', background: event.color, alignSelf: 'stretch', margin: '0 12px', marginBottom: '15px',}} />

                {/* Right side - Drinks */}
                <div style={{ padding: '12px 16px 16px 12px', flex: 1, overflowY: 'auto', maxHeight: '200px' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: getCalendarTaskColor(event.color), marginBottom: 10 }}>Drikke</p>
                  {eventMeals.filter(m => m.category?.includes('drink') || m.category?.includes('Drink') || m.category?.includes('drikke')).map(meal => (
                    <p key={meal.id} style={{ fontSize: 10, color: '#1a1a2e', marginBottom: 6 }}>{meal.name}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid-2">
          {/* Tasks */}
          <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color }}>
            <div className="card-header">
              <span className="card-title">Aktive opgaver</span>
              <button className="btn-ghost" onClick={() => navigate(`/event/${eventId}/todo`)}>se alle</button>
            </div>
            {eventTasks.slice(0, 5).map(task => (
              <div key={task.id} className="todo-item" onClick={() => setTaskModal(task)}>
                <div
                  className={`checkbox ${task.done ? 'checked' : ''}`}
                  onClick={e => { e.stopPropagation(); toggleTask(task.id) }}
                >
                  {task.done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={`todo-text ${task.done ? 'done' : ''}`}>{task.title}</div>
                  <div className="todo-meta">
                    <span className={`pill pill-${task.priority}`}>{task.priority === 'high' ? 'høj' : task.priority === 'medium' ? 'middel' : 'lav'}</span>
                    <span style={{ fontSize: 10, color: secondaryTextColor }}>{task.assignedTo}</span>
                    {task.done && <span className="pill pill-done">klaret</span>}
                    {!task.done && task.deadline && new Date(task.deadline) < new Date() && <span className="pill pill-overdue">forsinket</span>}
                  </div>
                </div>
              </div>
            ))}
            {eventTasks.length === 0 && <p style={{ fontSize: 12, color: secondaryTextColor, textAlign: 'center', padding: '16px 0' }}>ingen opgaver endnu</p>}
          </div>

          {/* Tidsplan */}
          <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color }}>
            <div className="card-header">
              <span className="card-title">Tidsplan</span>
              <button className="btn-ghost" onClick={() => navigate(`/event/${eventId}/tidsplan`)}>se alt</button>
            </div>
            {(() => {
              const eventDate = new Date(event.date)
              const displayDate = calendarMonth
              const year = displayDate.getFullYear()
              const month = displayDate.getMonth()
              const firstDay = new Date(year, month, 1).getDay()
              const daysInMonth = new Date(year, month + 1, 0).getDate()
              const startPad = firstDay === 0 ? 6 : firstDay - 1
              const monthName = displayDate.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })

              const today = new Date()
              const eventDay = eventDate.getDate()
              const eventMonth = eventDate.getMonth()
              const eventYear = eventDate.getFullYear()

              const taskDays = eventTasks.reduce((acc, t) => {
                if (t.deadline) {
                  const d = new Date(t.deadline)
                  if (d.getFullYear() === year && d.getMonth() === month) {
                    acc.add(d.getDate())
                  }
                }
                return acc
              }, new Set())

              const days = []
              for (let i = 0; i < startPad; i++) days.push(null)
              for (let i = 1; i <= daysInMonth; i++) days.push(i)

              const handlePrevMonth = () => {
                setCalendarMonth(new Date(year, month - 1, 1))
              }

              const handleNextMonth = () => {
                setCalendarMonth(new Date(year, month + 1, 1))
              }

              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#1a1a2e', fontWeight: 600, padding: 0 }}>&lt;</button>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#222222', margin: 0 }}>{monthName}</p>
                    <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#1a1a2e', fontWeight: 600, padding: 0 }}>&gt;</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 12 }}>
                    {['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'].map(day => (
                      <div key={day} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: getCalendarTaskColor(event.color), paddingBottom: 4 }}>{day}</div>
                    ))}
                    {days.map((day, idx) => {
                      const isEventDay = day === eventDay && year === eventYear && month === eventMonth
                      const isToday = day === today.getDate() && year === today.getFullYear() && month === today.getMonth()
                      return (
                        <div key={idx} style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, position: 'relative' }}>
                          {day ? (
                            <>
                              {isEventDay ? (
                                <div style={{ position: 'relative' }}>
                                  <span style={{ fontSize: 14, color: "#B8860B" }}>★</span>
                                  <span style={{ color: '#B8860B', fontWeight: 700, fontSize: 11, position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>{day}</span>
                                </div>
                              ) : (
                                <span style={{ color: '#1a1a2e', fontWeight: isToday ? 700 : 400 }}>{day}</span>
                              )}
                              {taskDays.has(day) && !isEventDay && <span style={{ color: "#B8860B", position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: getCalendarTaskColor(event.color) }} />}
                            </>
                          ) : (
                            <span style={{ color: '#f0f0f0' }}>.</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <p style={{ fontSize: 9, color: secondaryTextColor, textAlign: 'center' }}>★ = event • <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: getCalendarTaskColor(event.color), marginRight: 3, marginLeft: 3 }} /> = opgaver</p>
                </div>
              )
            })()}
          </div>
        </div>

        {/* Aktivitetslog */}
        <div className="card" style={{ background: event.color + '33', border: '4px solid ' + event.color }}>
          <div className="card-header">
            <span className="card-title">Aktivitetslog</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {(() => {
              const activities = []

              activities.push({
                type: 'event',
                text: 'Event oprettet',
                person: event.ownerName || event.organizerName || 'Arrangør',
                date: event.createdAt ? new Date(event.createdAt).toLocaleDateString('da-DK') : null,
                time: event.createdAt ? new Date(event.createdAt).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : null,
                order: 0
              })

              eventTasks.forEach((task, idx) => {
                activities.push({
                  type: 'task',
                  text: `"${task.title}" tilføjet`,
                  person: task.assignedTo || 'Ukendt',
                  date: task.createdAt ? new Date(task.createdAt).toLocaleDateString('da-DK') : null,
                  time: task.createdAt ? new Date(task.createdAt).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : null,
                  order: 1
                })
              })

              eventMeals.forEach((meal, idx) => {
                activities.push({
                  type: 'meal',
                  text: `"${meal.name}" tilføjet til madplan`,
                  person: meal.assignedTo || 'Ukendt',
                  date: meal.createdAt ? new Date(meal.createdAt).toLocaleDateString('da-DK') : null,
                  time: meal.createdAt ? new Date(meal.createdAt).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : null,
                  order: 3
                })
              })

              return activities.length > 1 ? (
                <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {activities.sort((a, b) => b.order - a.order).slice(0, 8).map((activity, idx) => (
                    <div key={idx} style={{ padding: '8px 0', borderBottom: '1px solid ' + event.color, fontSize: 11, flexShrink: 0 }}>
                      <p style={{ color: '#1a1a2e', fontWeight: 500, marginBottom: 2 }}>{activity.text}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <p style={{ fontSize: 9, color: secondaryTextColor }}>af {activity.person}</p>
                        {activity.date && <p style={{ fontSize: 9, color: secondaryTextColor }}>{activity.date}{activity.time && ` kl. ${activity.time}`}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 11, color: secondaryTextColor, textAlign: 'center', padding: '12px 0' }}>ingen aktivitet endnu</p>
              )
            })()}
          </div>
        </div>
      </div>

      {taskModal && taskModal !== 'new' && <TaskModal task={taskModal} onClose={() => setTaskModal(null)} />}
      {taskModal === 'new' && <TaskModal defaultEventId={eventId} onClose={() => setTaskModal(null)} />}
      {inviteOpen && <InviteModal eventName={event.name} eventId={event.id} onClose={() => setInviteOpen(false)} />}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Slet event?</div>
              <button className="modal-close" onClick={() => setDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#1a1a2e', marginBottom: 16 }}>
                Er du sikker på at du vil slette <strong>{event.name}</strong>?
              </p>
              <p style={{ fontSize: 12, color: secondaryTextColor }}>
                Dette kan ikke fortrydes.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setDeleteConfirm(false)}>Annuller</button>
              <button className="modal-confirm" onClick={async () => {
                try {
                  await deleteEvent(eventId)
                  navigate('/dashboard')
                } catch (err) {
                  console.error('Error deleting event:', err)
                }
              }} style={{ background: '#ff6b6b' }}>
                Slet
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}



